import { ApiClientError, auth } from "@lumberset/api-client";
import { Button, Field, Heading, Hint, Input } from "@lumberset/ui";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { meQuery } from "../../lib/queries";
import { isUnconfirmedEmail, UnconfirmedEmail } from "./-unconfirmed-email";

const STAFF_ONLY = "This panel is for LumberSet staff.";

interface LoginSearch {
  redirect?: string;
  /** Set by the set-password page once a new password is saved. */
  notice?: "password-set";
}

export const Route = createFileRoute("/_auth/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    notice: search.notice === "password-set" ? "password-set" : undefined,
  }),
  component: Login,
});

function Login() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const { staffOnly } = Route.useRouteContext();
  const [error, setError] = useState<string | null>(staffOnly ? STAFF_ONLY : null);
  /** The address to confirm, once a right password met an unconfirmed email. */
  const [unconfirmed, setUnconfirmed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const email = String(form.get("email"));
    setBusy(true);
    setError(null);
    setUnconfirmed(null);

    const result = await auth.signIn.email({
      email,
      password: String(form.get("password")),
    });

    if (result.error) {
      setBusy(false);
      if (isUnconfirmedEmail(result.error)) setUnconfirmed(email);
      else setError(result.error.message ?? "That email and password do not match.");
      return;
    }

    // A session alone is not enough: only Staff may see the shell.
    try {
      await queryClient.fetchQuery(meQuery);
    } catch (failure) {
      if (failure instanceof ApiClientError && failure.status === 403) {
        await auth.signOut();
        queryClient.clear();
        setBusy(false);
        setError(STAFF_ONLY);
        return;
      }
      setBusy(false);
      setError(failure instanceof Error ? failure.message : "Could not sign in.");
      return;
    }

    // Only follow a redirect back into this app, never to another site.
    const target = search.redirect?.startsWith("/") ? search.redirect : "/";
    await router.invalidate();
    router.history.push(target);
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-y-5">
      <div>
        <Heading>Sign in</Heading>
        <p className="txt-small mt-1 text-ui-fg-subtle">
          Review queue and support for every workspace.
        </p>
      </div>

      {search.notice === "password-set" && !error && !unconfirmed ? (
        <p className="txt-small rounded-lg bg-ui-bg-subtle px-3 py-2 text-ui-fg-base" role="status">
          Your password is saved. Sign in with it now.
        </p>
      ) : null}

      <Field label="Email">
        {(control) => (
          <Input
            {...control}
            name="email"
            type="email"
            autoComplete="email"
            required
            size="large"
            autoFocus
          />
        )}
      </Field>

      <Field label="Password">
        {(control) => (
          <Input
            {...control}
            name="password"
            type="password"
            autoComplete="current-password"
            required
            size="large"
          />
        )}
      </Field>

      {unconfirmed ? <UnconfirmedEmail email={unconfirmed} /> : null}
      {error ? <Hint variant="error">{error}</Hint> : null}

      <Button type="submit" size="large" block isLoading={busy}>
        Sign in
      </Button>

      <Link
        to="/forgot-password"
        className="txt-small text-center text-ui-fg-interactive hover:text-ui-fg-interactive-hover"
      >
        Forgot your password?
      </Link>
    </form>
  );
}
